<template>
    <section class="mobile-login-page">
        <header class="mobile-login-header">
            <button type="button" aria-label="返回" @click="goBack">
                <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="m15 5-7 7 7 7" />
                </svg>
            </button>
            <span>登录</span>
            <i></i>
        </header>

        <main class="mobile-login-main">
            <div class="mobile-login-brand">
                <div class="mobile-login-brand__logo">
                    <img src="/static/bbs/images/login/logo_black.png" alt="BIGEMAP">
                    <img src="/static/bbs/images/index/logo_text.png" alt="知识库">
                </div>
                <h1>欢迎回来</h1>
                <p>登录后可提问、回复，并管理你的产品服务</p>
            </div>

            <section class="mobile-login-card">
                <div class="mobile-login-tabs" role="tablist" aria-label="登录方式">
                    <button
                        v-for="item in loginTypes"
                        :key="item.name"
                        type="button"
                        role="tab"
                        :aria-selected="activeType === item.name"
                        :class="{'mobile-login-tabs__item--active': activeType === item.name}"
                        @click="changeLoginType(item.name)"
                    >
                        {{ item.title }}
                    </button>
                </div>

                <form v-if="activeType === 'code'" class="mobile-login-form" @submit.prevent="submitCodeLogin">
                    <label class="mobile-login-field">
                        <span>手机号</span>
                        <div>
                            <svg viewBox="0 0 24 24" aria-hidden="true">
                                <rect x="7" y="3" width="10" height="18" rx="2" />
                                <path d="M10 6h4M11 18h2" />
                            </svg>
                            <input
                                v-model.trim="codeForm.phone"
                                type="tel"
                                inputmode="numeric"
                                maxlength="11"
                                autocomplete="tel"
                                placeholder="请输入手机号"
                            >
                        </div>
                    </label>
                    <label class="mobile-login-field">
                        <span>验证码</span>
                        <div>
                            <svg viewBox="0 0 24 24" aria-hidden="true">
                                <path d="M7 10V8a5 5 0 0 1 10 0v2" />
                                <rect x="5" y="10" width="14" height="10" rx="2" />
                            </svg>
                            <input
                                v-model.trim="codeForm.code"
                                type="text"
                                inputmode="numeric"
                                maxlength="6"
                                autocomplete="one-time-code"
                                placeholder="请输入验证码"
                            >
                            <button
                                class="mobile-login-code-button"
                                type="button"
                                :disabled="sendingCode || countdown > 0"
                                @click="sendCode"
                            >
                                {{ countdown > 0 ? `${countdown}s 后重试` : sendingCode ? '发送中…' : '获取验证码' }}
                            </button>
                        </div>
                    </label>
                    <p class="mobile-login-error" :class="{'mobile-login-error--visible': errorMessage}" aria-live="polite">
                        {{ errorMessage || '提示信息占位' }}
                    </p>
                    <button class="mobile-login-submit" type="submit" :disabled="submitting || !agreed">
                        {{ submitting ? '正在登录…' : '登录' }}
                    </button>
                </form>

                <form v-else class="mobile-login-form" @submit.prevent="submitPasswordLogin">
                    <label class="mobile-login-field">
                        <span>用户名或手机号</span>
                        <div>
                            <svg viewBox="0 0 24 24" aria-hidden="true">
                                <circle cx="12" cy="8" r="3.25" />
                                <path d="M5.25 19.25c.55-3.35 3.05-5.25 6.75-5.25s6.2 1.9 6.75 5.25" />
                            </svg>
                            <input
                                v-model.trim="passwordForm.username"
                                type="text"
                                autocomplete="username"
                                placeholder="请输入用户名或手机号"
                            >
                        </div>
                    </label>
                    <label class="mobile-login-field">
                        <span>密码</span>
                        <div>
                            <svg viewBox="0 0 24 24" aria-hidden="true">
                                <path d="M7 10V8a5 5 0 0 1 10 0v2" />
                                <rect x="5" y="10" width="14" height="10" rx="2" />
                            </svg>
                            <input
                                v-model="passwordForm.password"
                                :type="passwordVisible ? 'text' : 'password'"
                                autocomplete="current-password"
                                placeholder="请输入密码"
                            >
                            <button class="mobile-login-password-toggle" type="button" :aria-label="passwordVisible ? '隐藏密码' : '显示密码'" @click="passwordVisible = !passwordVisible">
                                <svg viewBox="0 0 24 24" aria-hidden="true">
                                    <template v-if="passwordVisible">
                                        <path d="M3.5 12s3-5 8.5-5 8.5 5 8.5 5-3 5-8.5 5-8.5-5-8.5-5Z" />
                                        <circle cx="12" cy="12" r="2" />
                                    </template>
                                    <template v-else>
                                        <path d="M4 5 20 19M9.5 7.4A8.8 8.8 0 0 1 12 7c5.5 0 8.5 5 8.5 5a13.4 13.4 0 0 1-2.4 2.8M14.2 16.7A9 9 0 0 1 12 17c-5.5 0-8.5-5-8.5-5a13.7 13.7 0 0 1 2.3-2.7" />
                                    </template>
                                </svg>
                            </button>
                        </div>
                    </label>
                    <p class="mobile-login-error" :class="{'mobile-login-error--visible': errorMessage}" aria-live="polite">
                        {{ errorMessage || '提示信息占位' }}
                    </p>
                    <button class="mobile-login-submit" type="submit" :disabled="submitting || !agreed">
                        {{ submitting ? '正在登录…' : '登录' }}
                    </button>
                </form>

                <label class="mobile-login-agreement">
                    <input v-model="agreed" type="checkbox">
                    <span>我已阅读并同意《用户协议》和《隐私政策》</span>
                </label>
            </section>

            <div class="mobile-login-security">
                <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M12 3 5.5 5.5v5.25c0 4.2 2.65 7.75 6.5 9.25 3.85-1.5 6.5-5.05 6.5-9.25V5.5L12 3Z" />
                    <path d="m9.25 11.5 1.75 1.75 3.75-4" />
                </svg>
                <span>账号信息经过安全加密传输</span>
            </div>
        </main>
    </section>
</template>

<script>
import '@/components/mobile/vant';
import Cookie from 'js-cookie';
import storage from '@/utils/storage';
import {JUMP_URL} from '@/utils/eventList';

export default {
    name: 'MobileLogin',
    data() {
        return {
            activeType: 'code',
            loginTypes: [
                {title: '验证码登录', name: 'code'},
                {title: '账号密码登录', name: 'password'},
            ],
            codeForm: {
                phone: '',
                code: '',
            },
            passwordForm: {
                username: '',
                password: '',
            },
            passwordVisible: false,
            agreed: true,
            hasSentCode: false,
            sendingCode: false,
            submitting: false,
            countdown: 0,
            timer: null,
            errorMessage: '',
        };
    },
    beforeDestroy() {
        clearInterval(this.timer);
    },
    methods: {
        changeLoginType(type) {
            this.activeType = type;
            this.errorMessage = '';
        },
        goBack() {
            if (window.history.length > 1) {
                this.$router.back();
                return;
            }
            this.$router.push({path: '/index'});
        },
        sendCode() {
            if (!/^1[3-9]\d{9}$/.test(this.codeForm.phone)) {
                this.errorMessage = '请输入正确的手机号码';
                return;
            }
            if (this.sendingCode || this.countdown > 0) {
                return;
            }
            this.errorMessage = '';
            this.sendingCode = true;
            this.$bbs.post('/bbs/api/pco', {phone: this.codeForm.phone}, {
                need: {error: true, progress: true},
            }).then(() => {
                this.hasSentCode = true;
                this.countdown = 120;
                clearInterval(this.timer);
                this.timer = setInterval(() => {
                    if (this.countdown > 0) {
                        this.countdown -= 1;
                    } else {
                        clearInterval(this.timer);
                    }
                }, 1000);
                this.$message.success('验证码已发送');
            }).catch(() => {
                this.errorMessage = '验证码发送失败，请稍后重试';
            }).finally(() => {
                this.sendingCode = false;
            });
        },
        submitCodeLogin() {
            if (!/^1[3-9]\d{9}$/.test(this.codeForm.phone)) {
                this.errorMessage = '请输入正确的手机号码';
                return;
            }
            if (!this.hasSentCode || !this.codeForm.code) {
                this.errorMessage = '请先获取并输入验证码';
                return;
            }
            this.submitLogin('/bbs/api/vlog', this.codeForm);
        },
        submitPasswordLogin() {
            if (!this.passwordForm.username) {
                this.errorMessage = '请输入用户名或手机号';
                return;
            }
            if (!this.passwordForm.password) {
                this.errorMessage = '请输入密码';
                return;
            }
            this.submitLogin('/bbs/api/pu', this.passwordForm);
        },
        submitLogin(url, form) {
            if (!this.agreed) {
                this.errorMessage = '请先阅读并同意用户协议和隐私政策';
                return;
            }
            if (this.submitting) {
                return;
            }
            this.errorMessage = '';
            this.submitting = true;
            this.$bbs.post(url, form, {
                need: {error: true, progress: true},
            }).then((response) => {
                this.handleLoginSuccess(response);
            }).catch(() => {
                this.errorMessage = '登录失败，请检查输入后重试';
            }).finally(() => {
                this.submitting = false;
            });
        },
        handleLoginSuccess(response) {
            if (!response || response.code !== 200) {
                this.errorMessage = response && (response.msg || response.message) || '登录失败';
                return;
            }
            const data = response.data || {};
            const menu = Array.isArray(data.menu) ? data.menu.map((item) => ({
                ...item,
                url: item.url ? item.url.replace('.html', '') : item.url,
            })) : [];
            localStorage.removeItem(process.env.VUE_APP_ELSE_CACHE);
            this.$store.dispatch('userData', data.userData || {});
            storage.token(data.token);
            storage.storage(process.env.VUE_APP_UPLOAD_CODE, data.upload);
            storage.permission(menu);
            this.$store.dispatch('permission', menu);
            Cookie.set(process.env.VUE_APP_COOKIE, new Date().getTime());

            const cachedJump = storage.storage(JUMP_URL);
            storage.storage(JUMP_URL, '');
            let target = this.$route.query.redirect;
            if (!target && cachedJump && cachedJump.url) {
                target = cachedJump.url;
            }
            if (!target || target === '/login') {
                target = '/index';
            }
            this.$message.success('登录成功');
            this.$router.replace({path: target});
        },
    },
};
</script>

<style scoped lang="less">
.mobile-login-page {
    min-height: 100vh;
    color: #172033;
    background:
        radial-gradient(circle at 90% 6%, rgba(64, 145, 255, 0.15), transparent 31%),
        linear-gradient(180deg, #eef5ff 0, #f6f8fb 42%, #f6f8fb 100%);
}

.mobile-login-header {
    display: flex;
    height: 54px;
    padding: env(safe-area-inset-top) 14px 0;
    box-sizing: content-box;
    align-items: center;
    justify-content: space-between;

    button,
    i {
        width: 38px;
    }

    button {
        display: flex;
        height: 40px;
        padding: 0;
        align-items: center;
        color: #46546a;
        background: transparent;
        border: 0;
    }

    svg {
        width: 23px;
        height: 23px;
        fill: none;
        stroke: currentColor;
        stroke-width: 1.9;
        stroke-linecap: round;
        stroke-linejoin: round;
    }

    span {
        font-size: 16px;
        font-weight: 600;
    }
}

.mobile-login-main {
    width: 100%;
    max-width: 480px;
    margin: 0 auto;
    padding: 24px 18px calc(28px + env(safe-area-inset-bottom));
    box-sizing: border-box;
}

.mobile-login-brand {
    padding: 4px 4px 24px;

    h1 {
        margin: 24px 0 8px;
        font-size: 27px;
        font-weight: 650;
        letter-spacing: -0.5px;
    }

    > p {
        margin: 0;
        color: #69768b;
        font-size: 14px;
        line-height: 22px;
    }
}

.mobile-login-brand__logo {
    display: flex;
    align-items: center;
    gap: 9px;

    img:first-child {
        width: 124px;
        height: auto;
    }

    img:last-child {
        width: 56px;
        height: auto;
        transform: translateY(2px);
    }
}

.mobile-login-card {
    padding: 6px 18px 18px;
    background: rgba(255, 255, 255, 0.96);
    border: 1px solid rgba(220, 228, 239, 0.9);
    border-radius: 14px;
    box-shadow: 0 14px 34px rgba(47, 83, 132, 0.09);
}

.mobile-login-tabs {
    display: flex;
    margin-bottom: 8px;
    border-bottom: 1px solid #edf0f4;

    button {
        position: relative;
        height: 52px;
        padding: 0;
        flex: 1;
        color: #7a8698;
        font-size: 15px;
        font-weight: 500;
        background: transparent;
        border: 0;

        &::after {
            position: absolute;
            right: 28%;
            bottom: -1px;
            left: 28%;
            height: 3px;
            content: '';
            background: transparent;
            border-radius: 3px 3px 0 0;
        }
    }

    .mobile-login-tabs__item--active {
        color: #2478f2;

        &::after {
            background: #2478f2;
        }
    }
}

.mobile-login-form {
    padding-top: 8px;
}

.mobile-login-field {
    display: block;
    margin-top: 16px;

    > span {
        display: block;
        margin-bottom: 8px;
        color: #48566b;
        font-size: 13px;
        font-weight: 500;
    }

    > div {
        display: flex;
        height: 48px;
        padding: 0 13px;
        align-items: center;
        gap: 10px;
        background: #f7f9fc;
        border: 1px solid #e2e7ef;
        border-radius: 8px;

        &:focus-within {
            background: #fff;
            border-color: #8bb9fa;
            box-shadow: 0 0 0 3px rgba(36, 120, 242, 0.08);
        }
    }

    svg {
        width: 19px;
        height: 19px;
        flex-shrink: 0;
        fill: none;
        stroke: #7e8b9e;
        stroke-width: 1.7;
        stroke-linecap: round;
        stroke-linejoin: round;
    }

    input {
        width: 100%;
        min-width: 0;
        padding: 0;
        color: #202a3b;
        font-size: 15px;
        background: transparent;
        border: 0;
        outline: 0;
    }
}

.mobile-login-code-button,
.mobile-login-password-toggle {
    padding: 0;
    flex-shrink: 0;
    background: transparent;
    border: 0;
}

.mobile-login-code-button {
    min-width: 88px;
    color: #2478f2;
    font-size: 13px;

    &:disabled {
        color: #9aa5b5;
    }
}

.mobile-login-password-toggle {
    display: flex;
    width: 30px;
    height: 36px;
    align-items: center;
    justify-content: center;
}

.mobile-login-error {
    height: 18px;
    margin: 10px 0 -4px;
    overflow: hidden;
    visibility: hidden;
    color: #df4e4e;
    font-size: 12px;
    line-height: 18px;
    white-space: nowrap;
    text-overflow: ellipsis;
}

.mobile-login-error--visible {
    visibility: visible;
}

.mobile-login-submit {
    width: 100%;
    height: 48px;
    margin-top: 22px;
    color: #fff;
    font-size: 16px;
    font-weight: 600;
    background: linear-gradient(135deg, #3188f5, #166de5);
    border: 0;
    border-radius: 8px;
    box-shadow: 0 8px 18px rgba(36, 120, 242, 0.2);

    &:disabled {
        background: #b8c5d8;
        box-shadow: none;
        cursor: not-allowed;
    }
}

.mobile-login-agreement {
    display: flex;
    margin: 17px 2px 0;
    align-items: flex-start;
    gap: 8px;
    color: #7d899b;
    font-size: 11px;
    line-height: 17px;

    input {
        width: 15px;
        height: 15px;
        margin: 1px 0 0;
        accent-color: #2478f2;
    }
}

.mobile-login-security {
    display: flex;
    margin-top: 22px;
    align-items: center;
    justify-content: center;
    gap: 6px;
    color: #96a1b1;
    font-size: 11px;

    svg {
        width: 16px;
        height: 16px;
        fill: none;
        stroke: #70a2e6;
        stroke-width: 1.7;
        stroke-linecap: round;
        stroke-linejoin: round;
    }
}
</style>
